import React, {Fragment, useEffect, useState} from "react";
import ReactsCard from "../components/ui/ReactsCard";
import {TabData, Wizard} from "../components/_shared/wizard/Wizard";
import Typography from "@material-ui/core/Typography";
import {WizardNavigation, WizardNavigationButton} from "../components/_shared/wizard/WizardNavigation";
import {useTranslation} from "react-i18next";
import {makeStyles} from "@material-ui/core/styles";
import {Machine} from "../models/machine.models";
import Button from "@material-ui/core/Button";
import {FormStatus} from "../models/shared";
import * as _ from "lodash";
import {addContacts, searchOrganizationMachines} from "../api/machine-functions";
import {AddMachineForm, AddMachineFormType, MachineToAddModel, SearchedMember} from "../models/membership.models";
import {httpPost} from "../api/http-functions";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {getOrganization, searchOrganizationMembers} from "../api/organization-functions";
import {ArrowBackIos} from "@material-ui/icons";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import {useOrganization} from "../components/organizations/organization-context";
import SelectMachines from "../components/members/add-machines/SelectMachines";
import AddMachinesValidation from "../components/members/add-machines/AddMachinesValidation";
import AddMachinesByEmail from "../components/members/add-machines/AddMachineByEmail";

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(4),
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        '& > *': {
            'margin-bottom': theme.spacing(4)
        }
    },
    selectedCount: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
    },
    formsContainer: {
        marginTop: theme.spacing(2)
    },
    addBtn: {
        marginTop: theme.spacing(4)
    },
    actions: {
        '& > *': {
            marginLeft: theme.spacing(2)
        }
    },
    actionContainer: {
        width: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    deleteBtn: {
        color: theme.palette.error.main
    },
    itemContainer:{
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1)
    },
    errorBanner: {
        width: '100%',
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText
    },
    top: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        alignItems: 'center'
    }
}));

enum addContactResult {
    unknown = 0,
    success = 1,
    unauthorized = 2,
    sourceNotSupportPairing = 3,
    targetNotSupportPairing = 4,
    failed = 99
}

const MemberAddMachinesView: React.FC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const {memberId} = useParams<{ memberId: string }>();
    const [searchParams] = useSearchParams();
    const organizationId = searchParams.get("organizationId");
    const [organizationName, setOrganizationName] = useState('');
    const [member, setMember] = useState<SearchedMember | undefined>();
    const [selectedMachines, setSelectedMachines] = useState<Machine[]>([]);
    const [machineEmails, setMachineEmails] = useState<MachineToAddModel[]>([]);
    const [forms, setForms] = useState<AddMachineForm[]>([]);
    const [currentOrganization, setOrganization] = useOrganization();
    const navigate = useNavigate();

    useEffect(() => {
        findMember().then((result) => {
            if (result === null || organizationId === '3528') {
                // Do not allow if organization is Philips or the user does not exist
                navigate(-1);
            } else {
                getOrganization(parseInt(organizationId || '0')).then(org => {
                    setOrganization(org);
                    setOrganizationName(org.name);
                });
            }
        });
    }, []);

    useEffect(() => {
        for (const selectedMachine of selectedMachines) {
            const currentSelectedMachine = forms.some(f => f.id === selectedMachine.id && f.type === AddMachineFormType.FromSearch);
            if (!currentSelectedMachine) {
                setForms(prev => [...prev, { id: selectedMachine.id, type: AddMachineFormType.FromSearch, status: FormStatus.deletable, data: selectedMachine, errors: {}, errorMessage: undefined }]);
            }
        }
    }, [selectedMachines]);

    useEffect(() => {
        for (const machineEmail of machineEmails) {
            const currentMachineEmail = forms.some(f => f.id === machineEmail.id && f.type === AddMachineFormType.FromEmail && machineEmail.email);
            if (!currentMachineEmail) {
                setForms(prev => [...prev, {id: machineEmail.id, type: AddMachineFormType.FromEmail, status: FormStatus.deletable, data: machineEmail, errors: {}, errorMessage: undefined }]);
            }
        }
    }, [machineEmails]);

    useEffect(() => {
        //Remove the failed entries that were removed from the other tabs
        for (const form of forms) {
            if (form.status !== FormStatus.successful) {
                if (form.type === AddMachineFormType.FromSearch && !selectedMachines.some(x => x.id === form.id)) {
                    setForms(prevState => prevState.filter(f => f.id !== form.id || f.type !== form.type));
                } else if (form.type === AddMachineFormType.FromEmail && !machineEmails.some(x => x.id === form.id && x.email)) {
                    setForms(prevState => prevState.filter(f => f.id !== form.id || f.type !== form.type));
                } else if (form.type === AddMachineFormType.FromEmail && machineEmails.some(x => x.id === form.id && x.email && x.email !== form.data.email)) {
                    //Update forms to use updated emails
                    setForms(prevState => {
                        const currentIndex = prevState.findIndex(f => f.id === form.id && f.type === form.type);
                        const current = prevState[currentIndex];
                        current.data.email = machineEmails.filter(x => x.id === form.id)[0].email;
                        const result = [...prevState];
                        result[currentIndex] = {id: current.id, type: current.type, status: current.status, data: machineEmails.filter(x => x.id === form.id)[0], errors: current.errors, errorMessage: current.errorMessage};
                        return result;
                    });
                }
            }
        }
    }, [forms]);

    const findMember = async (page: number = 0, size: number = 100): Promise<SearchedMember | null> => {
        const results = await searchOrganizationMembers(httpPost)({organizationId: organizationId && parseInt(organizationId) || undefined, filter: "", page: page, size: size});
        if (results.items.length === 0) {
            navigate(-1)
            return null;
        }

        const foundMember = results.items.filter((x: SearchedMember) => x.userId === memberId);
        if (foundMember.length > 0) {
            setMember(foundMember[0]);
            return foundMember[0];
        } else {
            const found = await findMember(page + 1, size);
            if (found) return found;
        }
        return null;
    };

    const onClose = () => {
        navigate(-1);
    };

    const goToOrganization = () => {
        navigate(-1);
    }

    const updateItemStatus = (item: AddMachineForm, status: FormStatus) => {
        setForms(prev => {
            const currentIndex = _.findIndex(prev, (x: AddMachineForm) => x === item);
            const result = [...prev];
            result[currentIndex] = _.merge({}, prev[currentIndex], {...item, status: status});
            return result;
        });
    };

    const updateItem = (item: AddMachineForm, fields: Partial<AddMachineForm>) => {
        setForms(prev => {
            const currentIndex = _.findIndex(prev, (x: AddMachineForm) => x === item);
            const result = [...prev];
            result[currentIndex] = _.merge({}, prev[currentIndex], {...item, ...fields});
            return result;
        });
    };

    const handleSave = async (form: AddMachineForm, machineId: string) => {
        const success = await addContacts(machineId, [{email: member?.email ?? "", firstName: member?.userFirstName || "", lastName: member?.userLastName || ""}]);
        if (success.Result === addContactResult.success) {
            updateItemStatus(form, FormStatus.successful);
        } else {
            const itemResults = success.ContactElementResponses.filter(r => r.Email === form.data.email);
            if (itemResults.length > 0) {
                const itemResult = itemResults[0];
                if (itemResult.Result === addContactResult.success) {
                    updateItemStatus(form, FormStatus.successful);
                } else if (itemResult.Result === addContactResult.sourceNotSupportPairing) {
                    updateItem(form, {status: FormStatus.failed, errorMessage: 'Machine.Contacts.AddContacts.SourceMachineCannotBePaired'});
                } else if (itemResult.Result === addContactResult.targetNotSupportPairing) {
                    updateItem(form, {status: FormStatus.failed, errorMessage: 'Machine.Contacts.AddContacts.TargetMachineCannotBePaired'});
                } else {
                    updateItemStatus(form, FormStatus.failed);
                }
            } else {
                updateItemStatus(form, FormStatus.failed);
            }
        }
    };

    const onSave = async () => {
        for (const form of forms) {
            if (form.status !== FormStatus.successful && form.status !== FormStatus.processing) {
                updateItemStatus(form, FormStatus.processing);
            }
        }
    };

    useEffect(() => {
        for (const form of forms) {
            if (form.status !== FormStatus.processing) continue;
            if (form.type === AddMachineFormType.FromSearch) {
                handleSave(form, (form.data as Machine).id).then(() => {});
            } else {
                searchOrganizationMachines(httpPost)({organizationId: undefined, filter: (form.data as MachineToAddModel).email, size: 1, page: 0}).then(async machineToSearch =>{
                    if (machineToSearch.total === 0) {
                        updateItem(form, {status: FormStatus.failed, errorMessage: 'Machine.Contacts.AddContacts.MachineDoesNotExist'});
                    } else {
                        await handleSave(form, machineToSearch.items[0].id);
                    }
                });
            }
        }
    }, [forms]);

    const getDisabledButtons = (): Set<WizardNavigationButton> => {
        if (forms.some(x => x.status === FormStatus.processing)) {
            return new Set<WizardNavigationButton>([WizardNavigationButton.Next, WizardNavigationButton.Previous, WizardNavigationButton.Close, WizardNavigationButton.Save]);
        } else if (forms.some(x => x.status === FormStatus.successful || x.status === FormStatus.failed)) {
            return new Set<WizardNavigationButton>([WizardNavigationButton.Next, WizardNavigationButton.Previous, WizardNavigationButton.Save])
        }
        return new Set<WizardNavigationButton>();
    };

    const step1: TabData = {
        key: "selectMachines",
        isDisabled: true,
        preserveTab: true,
        labelKey: "Machine.MoveMachines.Tabs.SelectMachines.Title",
        childComponent: (index, lastIndex, onChangeTab) => <Fragment>
            <SelectMachines selectedMachines={selectedMachines} setSelectedMachines={setSelectedMachines} organizationId={organizationId}></SelectMachines>
            <WizardNavigation onChangeTab={onChangeTab} onClose={onClose} lastIndex={lastIndex} index={index} />
        </Fragment>
    };

    const step2: TabData = {
        key: "addMachinesByEmail",
        isDisabled: true,
        preserveTab: true,
        labelKey: "Machine.Contacts.AddContacts.Tabs.ByEmail.Title",
        childComponent: (index, lastIndex, onChangeTab) => <Fragment>
            <AddMachinesByEmail machineEmails={machineEmails} setMachineEmails={setMachineEmails} forms={forms}></AddMachinesByEmail>
            <WizardNavigation onChangeTab={onChangeTab} onClose={onClose} index={index} lastIndex={lastIndex}></WizardNavigation>
        </Fragment>
    };

    const step3: TabData = {
        key: "validation",
        isDisabled: true,
        preserveTab: true,
        labelKey: "Machine.Contacts.AddContacts.Tabs.Validation.Title",
        childComponent: (index, lastIndex, onChangeTab) => {
            return <Fragment>
                <AddMachinesValidation forms={forms} setForms={setForms} setMachineEmails={setMachineEmails} setSelectedMachines={setSelectedMachines}></AddMachinesValidation>
                <WizardNavigation onChangeTab={onChangeTab} onClose={onClose} index={index} lastIndex={lastIndex} onSave={onSave} disabledButtons={getDisabledButtons()}></WizardNavigation>
            </Fragment>
        }
    };

    return <div className={classes.root}>
        <div className={classes.top}>
            <Button onClick={goToOrganization}>
                <ArrowBackIos color="primary"/>
            </Button>
            <Breadcrumbs>
                <Typography color="textPrimary" variant="body1">{organizationName}</Typography>
            </Breadcrumbs>
        </div>
        <ReactsCard title={t('Machine.Contacts.AddMachines.Title')}>
            <Wizard tabs={[step1, step2, step3]}></Wizard>
        </ReactsCard>
    </div>;
};

export default MemberAddMachinesView;
